/**
 * Dev mode → Settings: AI keys (kiaan-create-agent-v2.html §2) with the guided
 * "Connect Claude" / "Connect OpenRouter" flow — 3 steps, one test call, green
 * tick. A broken key shows a red notice + "Fix it".
 *
 * Review Focus 5: the browser never shows a raw provider error. The notice text
 * is fixed here; whatever the server sends back is not rendered.
 */
import { useState } from 'react';
import { getDevSettings, setProviderKey, type DevSettings, type Provider } from '../kiaanApi';
import { DevState, useDevData } from './useDevData';

export const KEY_BROKEN_TEXT = 'Key did not work. Check it and try again.';

const META: Record<Provider, { title: string; where: string; prefix: string; url: string }> = {
  claude: { title: 'Claude (Claude SDK)', where: 'console.anthropic.com → API keys', prefix: 'sk-ant-', url: 'https://console.anthropic.com/settings/keys' },
  openrouter: { title: 'OpenRouter', where: 'openrouter.ai → Keys', prefix: 'sk-or-', url: 'https://openrouter.ai/settings/keys' },
};

export function Settings({ canManageKeys }: { canManageKeys: boolean }) {
  const { data, loading, error, reload } = useDevData(getDevSettings);
  if (!data) return <DevState loading={loading} error={error} />;
  return (
    <div className="kw-grid">
      <div className="kw-box" style={{ gridColumn: '1 / 3' }}>
        <h4>AI keys (who pays for the AI)</h4>
        <ProviderCard provider="openrouter" state={data.openrouter} canManage={canManageKeys} onSaved={reload} />
        <ProviderCard provider="claude" state={data.claude} canManage={canManageKeys} onSaved={reload} />
        <div className="kw-small" style={{ marginTop: 6 }}>Keys stay on this server only. Spend shows here and in your credits.</div>
      </div>
      <div className="kw-box">
        <h4>Branding</h4>
        <div className="kw-row">Name shown <span className="kw-pill kw-ok kw-ml">Kiaan</span></div>
        <div className="kw-row">Task board (runs in background) <span className="kw-pill kw-ok kw-ml">Kiaan Work</span></div>
        <div className="kw-small">The task board is Paperclip (MIT licence), renamed.</div>
      </div>
    </div>
  );
}

function ProviderCard({ provider, state, canManage, onSaved }: { provider: Provider; state: DevSettings[Provider]; canManage: boolean; onSaved: () => void }) {
  const meta = META[provider];
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<'ok' | 'broken' | null>(null);

  const label = provider === 'claude' ? 'Connect Claude' : 'Connect OpenRouter';
  const masked = state.last4 ? `${meta.prefix}••••••••••••${state.last4}` : null;
  const broken = state.status === 'broken' && result !== 'ok';

  const submit = async () => {
    const k = key.trim();
    if (!k || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await setProviderKey(provider, k);
      setResult(r.status === 'ok' ? 'ok' : 'broken');
      if (r.status === 'ok') { setKey(''); setOpen(false); onSaved(); }
    } catch {
      setResult('broken');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }} data-testid={`provider-${provider}`}>
      <div className="kw-row"><b>{meta.title}</b>
        {state.status === 'ok' && <span className="kw-pill kw-ok kw-ml">✓ connected</span>}
        {state.status === 'broken' && <span className="kw-pill kw-bad kw-ml">key broken</span>}
        {state.status === 'unset' && <span className="kw-pill kw-wt kw-ml">not connected</span>}
      </div>
      {masked && <div className="kw-key">{masked}</div>}
      {broken && (
        <div className="kw-banner kw-danger" role="alert" style={{ margin: '6px 0 0' }}>
          {KEY_BROKEN_TEXT}{' '}
          {canManage && <button type="button" className="kw-btn kw-light" style={{ marginTop: 0, marginLeft: 6 }} onClick={() => setOpen(true)}>Fix it</button>}
        </div>
      )}
      {result === 'ok' && <div className="kw-banner kw-ok" role="status" style={{ margin: '6px 0 0' }}>✓ Key works. Saved.</div>}
      {canManage && !open && !broken && (
        <button type="button" className="kw-btn kw-light" onClick={() => { setOpen(true); setResult(null); }}>
          {state.status === 'ok' ? 'Change key' : label}
        </button>
      )}
      {open && (
        <div style={{ marginTop: 6 }} data-testid={`connect-${provider}`}>
          <div className="kw-step"><span className="kw-num">1</span>Get a key from {meta.where}. <a href={meta.url} target="_blank" rel="noreferrer" className="kw-small">Open</a></div>
          <div className="kw-step"><span className="kw-num">2</span>
            <input
              type="password"
              className="kw-fld"
              style={{ margin: 0 }}
              placeholder={`${meta.prefix}…`}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              aria-label={`${meta.title} key`}
              autoComplete="off"
            />
          </div>
          <div className="kw-step"><span className="kw-num">3</span>
            <span>
              Test and save.{' '}
              <button type="button" className="kw-btn" style={{ marginTop: 0 }} onClick={() => void submit()} disabled={!key.trim() || busy}>{busy ? 'Testing…' : 'Test and save'}</button>
              <button type="button" className="kw-btn kw-light" style={{ marginTop: 0 }} onClick={() => { setOpen(false); setKey(''); }}>Cancel</button>
            </span>
          </div>
          {result === 'broken' && (
            <div className="kw-banner kw-danger" role="alert" style={{ margin: '6px 0 0' }}>{KEY_BROKEN_TEXT}</div>
          )}
        </div>
      )}
    </div>
  );
}
